import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { RelativeTime } from '@/components/common/relative-time';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';
import { EmptyState } from '@/components/feedback/EmptyState';
import { SearchField, NoFilterResults } from '@/components/filters';
import { PageShell } from '@/components/layout/PageShell';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { EventDetailDialog } from '@/features/odoo/components/EventDetailDialog';
import { OdooSectionNav } from '@/features/odoo/components/OdooSectionNav';
import { SyncEventsTable } from '@/features/odoo/components/SyncEventsTable';
import { SyncStatStrip } from '@/features/odoo/components/SyncStatStrip';
import { eventActions } from '@/features/odoo/event-detail';
import { eventTypeKey } from '@/features/odoo/events';
import {
  useOdooEvents,
  useOdooEventStats,
  useOdooPermissions,
  useOdooStatus,
  useRetryAbandonedOdooEvents,
  useRetryOdooEvent,
} from '@/features/odoo/queries';
import { deriveIntegrationState } from '@/features/odoo/status';
import {
  DEFAULT_SYNC_TAB,
  filtersFromSearch,
  SYNC_PAGE_SIZE,
  SyncSearchSchema,
  type SyncTab,
} from '@/features/odoo/sync-search';
import { ODOO_EVENT_TYPES, type OdooEvent } from '@/features/odoo/types';
import { contractDisplayNumber } from '@/features/contracts/contract';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useToast } from '@/hooks/use-toast';
import { apiErrorMessage } from '@/lib/api';
import {
  ArrowLeft,
  CheckCircle2,
  Info,
  RefreshCw,
  RotateCw,
  X,
} from '@/lib/icons';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/_layout/settings/odoo/sync')({
  validateSearch: SyncSearchSchema,
  component: OdooSyncHealthPage,
});

const ALL_TYPES = '__all__';

const TAB_KEYS: Record<SyncTab, string> = {
  abandoned: 'odoo.sync.tabs.abandoned',
  pending: 'odoo.sync.tabs.pending',
  sent: 'odoo.sync.tabs.sent',
  all: 'odoo.sync.tabs.all',
};

const EMPTY_KEYS: Record<SyncTab, string> = {
  abandoned: 'odoo.sync.empty.attentionBody',
  pending: 'odoo.sync.empty.pending',
  sent: 'odoo.sync.empty.sent',
  all: 'odoo.sync.empty.all',
};

const TAB_ORDER: ReadonlyArray<SyncTab> = ['abandoned', 'pending', 'sent', 'all'];

function TabButton({
  active,
  label,
  count,
  onClick,
}: Readonly<{
  active: boolean;
  label: string;
  count?: number;
  onClick: () => void;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        active
          ? 'border-brand-500 bg-brand-50 text-foreground'
          : 'border-border-light bg-card text-muted-foreground hover:bg-surface-container-low/60'
      )}
    >
      {label}
      {count !== undefined ? (
        <span className="rounded-full bg-muted px-1.5 text-[11px] tabular-nums">
          {count}
        </span>
      ) : null}
    </button>
  );
}

function OdooSyncHealthPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const { canUpdate } = useOdooPermissions();

  const tab = search.status ?? DEFAULT_SYNC_TAB;
  const page = search.page ?? 1;
  const filters = filtersFromSearch(search);

  const [searchInput, setSearchInput] = useState(search.search ?? '');
  const debouncedSearch = useDebouncedValue(searchInput, 350).trim();

  const status = useOdooStatus({ poll: true });
  const stats = useOdooEventStats();
  const events = useOdooEvents(filters);
  const retryEvent = useRetryOdooEvent();
  const retryAll = useRetryAbandonedOdooEvents();

  const [bulkType, setBulkType] = useState<string | undefined>();
  const [selected, setSelected] = useState<OdooEvent | null>(null);

  const goTo = (
    next: Partial<{ status: SyncTab; eventType: string; page: number; search: string; contractId: string }>
  ) =>
    navigate({
      to: '/settings/odoo/sync',
      search: (prev) => {
        const merged = { ...prev, page: undefined, ...next };
        return Object.fromEntries(
          Object.entries(merged).filter(
            ([, value]) => value !== undefined && value !== ''
          )
        );
      },
      replace: true,
    });

  // The box owns the text while typing; the URL follows the debounce, and an
  // outside change (a link to "this record's events") is adopted into the box.
  const lastPushed = useRef(search.search ?? '');
  useEffect(() => {
    const fromUrl = search.search ?? '';
    if (fromUrl === lastPushed.current) return;
    lastPushed.current = fromUrl;
    setSearchInput(fromUrl);
  }, [search.search]);

  useEffect(() => {
    if (debouncedSearch === lastPushed.current) return;
    lastPushed.current = debouncedSearch;
    void goTo({ search: debouncedSearch });
    // goTo is rebuilt each render; only a settled term should touch the URL.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const retryOne = useConfirmDialog({
    title: t('odoo.sync.retryConfirm.title'),
    description: t('odoo.sync.retryConfirm.description'),
    confirmText: t('odoo.sync.retry'),
  });

  const abandonedForScope = (type: string | undefined): number => {
    if (!type) return stats.data?.byStatus.abandoned ?? status.data?.counts.abandoned ?? 0;
    return stats.data?.byEventType.find((e) => e.eventType === type)?.abandoned ?? 0;
  };
  const bulkCount = abandonedForScope(bulkType);

  const retryAllDialog = useConfirmDialog({
    variant: 'destructive',
    title: t('odoo.sync.bulk.title'),
    description: t('odoo.sync.bulk.description', { count: bulkCount }),
    confirmText: t('odoo.sync.bulk.confirm', { count: bulkCount }),
  });

  const handleRetry = async (event: OdooEvent) => {
    if (
      eventActions(event, { canUpdate }).retryNeedsConfirm &&
      !(await retryOne.confirm())
    ) {
      return;
    }
    retryEvent.mutate(event.id, {
      onSuccess: () =>
        toast({
          title: t('odoo.sync.toast.retried'),
          description: t('odoo.sync.toast.retriedBody'),
        }),
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: t('odoo.sync.toast.retryFailed'),
          description: apiErrorMessage(error),
        }),
    });
  };

  const handleRetryAll = async () => {
    const scope = search.eventType ?? undefined;
    setBulkType(scope);
    if (!(await retryAllDialog.confirm())) return;
    retryAll.mutate(scope, {
      onSuccess: (result) =>
        toast({
          title: t('odoo.sync.toast.bulkDone', { count: result.requeued }),
          description:
            result.remaining > 0
              ? t('odoo.sync.toast.bulkRemaining', { count: result.remaining })
              : undefined,
        }),
      onError: (error) =>
        toast({
          variant: 'destructive',
          title: t('odoo.sync.toast.retryFailed'),
          description: apiErrorMessage(error),
        }),
    });
  };

  const data = events.data;
  const rows = data?.items ?? [];
  const selectedEvent = selected
    ? (rows.find((row) => row.id === selected.id) ?? selected)
    : null;
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / SYNC_PAGE_SIZE));
  const firstRow = total === 0 ? 0 : (page - 1) * SYNC_PAGE_SIZE + 1;
  const lastRow = Math.min(page * SYNC_PAGE_SIZE, total);
  const isFiltered = Boolean(search.eventType || search.search || search.contractId);

  const integration = status.data ? deriveIntegrationState(status.data) : 'live';
  const abandonedAll = stats.data?.byStatus.abandoned ?? status.data?.counts.abandoned ?? 0;
  const counts = stats.data?.byStatus ?? status.data?.counts;
  const tabCount: Partial<Record<SyncTab, number>> = counts
    ? {
        abandoned: counts.abandoned,
        pending: counts.pending,
        sent: counts.sent,
        all: counts.abandoned + counts.pending + counts.sent,
      }
    : {};

  const refresh = () => {
    void events.refetch();
    void stats.refetch();
    void status.refetch();
  };

  return (
    <PageShell
      title={t('odoo.sync.title')}
      description={t('odoo.sync.description')}
      actions={
        <>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings/odoo">
              <ArrowLeft className="h-4 w-4" aria-hidden />
              {t('odoo.sync.backToSettings')}
            </Link>
          </Button>
          {canUpdate && abandonedAll > 0 ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={retryAll.isPending}
              onClick={() => void handleRetryAll()}
            >
              <RotateCw className="h-4 w-4" aria-hidden />
              {t('odoo.sync.bulk.action', {
                count: abandonedForScope(search.eventType ?? undefined),
              })}
            </Button>
          ) : null}
        </>
      }
    >
      <OdooSectionNav current="sync" />

      {integration !== 'live' ? (
        <Alert variant={integration === 'shadow' ? 'info' : 'warning'}>
          <Info className="h-4 w-4" aria-hidden />
          <AlertDescription>
            <p>
              {integration === 'shadow'
                ? t('odoo.sync.held.shadow')
                : t('odoo.sync.held.disabled')}{' '}
              <Link
                to="/settings/odoo"
                className="font-medium underline underline-offset-2"
              >
                {t('odoo.sync.held.cta')}
              </Link>
            </p>
          </AlertDescription>
        </Alert>
      ) : null}

      <SyncStatStrip
        counts={counts}
        oldestPendingAt={status.data?.oldestPendingAt}
        oldestPendingAgeSeconds={status.data?.oldestPendingAgeSeconds}
        isLoading={stats.isLoading && status.isLoading}
      />

      <section className="overflow-hidden rounded-lg border border-border-light bg-card shadow-xs">
        <div className="space-y-3 border-b border-border-light bg-surface-container-low/60 px-4 py-3 md:px-5 md:py-3.5">
          <div
            role="group"
            aria-label={t('odoo.sync.tabsLabel')}
            className="flex flex-wrap items-center gap-2"
          >
            {TAB_ORDER.map((value) => (
              <TabButton
                key={value}
                active={value === tab}
                label={t(TAB_KEYS[value])}
                count={tabCount[value]}
                onClick={() => goTo({ status: value })}
              />
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder={t('odoo.sync.searchPlaceholder')}
              isPending={searchInput.trim() !== debouncedSearch}
              className="min-w-[14rem] max-w-md"
            />
            <Select
              value={search.eventType ?? ALL_TYPES}
              onValueChange={(value) =>
                goTo({ eventType: value === ALL_TYPES ? undefined : value })
              }
            >
              <SelectTrigger
                aria-label={t('odoo.sync.eventTypeFilter')}
                className="w-56"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_TYPES}>
                  {t('odoo.sync.allEventTypes')}
                </SelectItem>
                {ODOO_EVENT_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(eventTypeKey(type) ?? type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {search.contractId ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 py-1 pe-1 ps-3 text-xs font-medium">
                {t('odoo.sync.contractFilter', {
                  number: contractDisplayNumber(search.contractId),
                })}
                <button
                  type="button"
                  onClick={() => void goTo({ contractId: undefined })}
                  aria-label={t('odoo.sync.clearContractFilter')}
                  className="rounded-full p-0.5 hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                </button>
              </span>
            ) : null}
            <div className="ms-auto flex items-center gap-2 text-xs text-muted-foreground">
              {events.dataUpdatedAt ? (
                <span aria-live="off">
                  {t('odoo.sync.updated')}{' '}
                  <RelativeTime value={events.dataUpdatedAt} />
                </span>
              ) : null}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={refresh}
                aria-label={t('odoo.sync.refresh')}
                title={t('odoo.sync.refresh')}
              >
                <RefreshCw
                  className={cn('h-4 w-4', events.isFetching && 'animate-spin')}
                  aria-hidden
                />
              </Button>
            </div>
          </div>
        </div>

        {events.isLoading ? (
          <div className="space-y-2 p-4" aria-busy="true">
            {Array.from({ length: 5 }, (_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : events.isError ? (
          <div className="p-4">
            <ApiErrorCard
              error={events.error}
              onRetry={refresh}
              title={t('odoo.sync.loadFailed')}
            />
          </div>
        ) : rows.length === 0 ? (
          isFiltered ? (
            <div className="p-4">
              <NoFilterResults
                isFiltered
                emptyTitle={t('odoo.sync.empty.all')}
                onReset={() => {
                  setSearchInput('');
                  void navigate({
                    to: '/settings/odoo/sync',
                    search: { status: search.status },
                    replace: true,
                  });
                }}
              />
            </div>
          ) : tab === 'abandoned' ? (
            <EmptyState
              icon={<CheckCircle2 className="h-6 w-6" />}
              title={t('odoo.sync.empty.attentionTitle')}
              description={t('odoo.sync.empty.attentionBody')}
            />
          ) : (
            <EmptyState
              title={t('odoo.sync.empty.title')}
              description={t(EMPTY_KEYS[tab])}
            />
          )
        ) : (
          <SyncEventsTable
            events={rows}
            odooUrl={status.data?.odooUrl}
            canRetry={canUpdate}
            retryingId={retryEvent.isPending ? (retryEvent.variables ?? null) : null}
            onRetry={(event) => void handleRetry(event)}
            onSelect={setSelected}
          />
        )}

        {rows.length > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border-light px-4 py-3 md:px-5">
            <span className="text-sm text-muted-foreground">
              {t('odoo.sync.showing', { from: firstRow, to: lastRow, total })}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => goTo({ page: page - 1 })}
              >
                {t('common.actions.previous')}
              </Button>
              <Badge variant="outline" size="sm" className="tabular-nums">
                {page} / {totalPages}
              </Badge>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => goTo({ page: page + 1 })}
              >
                {t('common.actions.next')}
              </Button>
            </div>
          </div>
        ) : null}
      </section>

      <EventDetailDialog
        event={selectedEvent}
        odooUrl={status.data?.odooUrl}
        canUpdate={canUpdate}
        retrying={
          retryEvent.isPending && retryEvent.variables === selectedEvent?.id
        }
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
        onRetry={(event) => void handleRetry(event)}
      />
      <retryOne.ConfirmDialog />
      <retryAllDialog.ConfirmDialog />
    </PageShell>
  );
}
